import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';

import { colors, spacing, typo, paperSx, primaryButtonSx, secondaryButtonSx } from '../theme/tokens';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional custom fallback renderer. Useful for sub-trees that want different recovery UI. */
  fallback?: (err: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level React error boundary.
 *
 * Catches render/lifecycle errors in its subtree and shows a recoverable
 * fallback UI. In development, also shows the error stack inside a
 * collapsible <details> block for debugging.
 *
 * Logs to console.error for now — swap in Sentry or another reporter later.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error:', error, info);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    const err = error ?? new Error('Unknown error');

    if (fallback) {
      return fallback(err, this.handleReset);
    }

    const isDev = process.env.NODE_ENV !== 'production';
    const message = err.message || 'Unknown error';

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.bgPage,
          p: spacing.xxxl,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...paperSx,
            maxWidth: 640,
            width: '100%',
            mb: 0,
          }}
        >
          <Typography sx={{ ...typo.pageTitle, mb: spacing.lg }}>
            Something went wrong
          </Typography>
          <Typography sx={{ ...typo.bodyText, color: colors.textSecondary, mb: spacing.xxl }}>
            {message}
          </Typography>

          {isDev && err.stack && (
            <Box
              component="details"
              sx={{
                mb: spacing.xxl,
                p: spacing.lg,
                borderRadius: '8px',
                backgroundColor: colors.bgCode,
                border: `1px solid ${colors.borderLight}`,
              }}
            >
              <Box component="summary" sx={{ ...typo.secondaryText, cursor: 'pointer', mb: spacing.sm }}>
                Stack trace (development)
              </Box>
              <Box
                component="pre"
                sx={{
                  ...typo.mono,
                  color: colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  m: 0,
                }}
              >
                {err.stack}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              disableElevation
              onClick={this.handleReload}
              sx={primaryButtonSx}
            >
              Reload page
            </Button>
            <Button
              variant="outlined"
              onClick={this.handleReset}
              sx={secondaryButtonSx}
            >
              Try again
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
